import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { addValuationDocument, setInspectionAddress, updateDeliveryDate, updateValuationCustomer } from '../../models/valuation-request';
import { slideInOutAnimation } from '../../route-animations';
import { storedValudationData } from '../../models/urlAuthParams.model';
import { valuationByHash } from '../../models/valuation.model';
import { ApiService } from '../../services/api.service';
import { ValuationService } from '../../services/valuation.service';
import moment from 'moment';
import { ValidatorService } from 'angular-iban';
import heic2any from 'heic2any';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SHARED_IMPORTS } from '../../shared/standalone-imports';

@Component({
  selector: 'app-request-information',
  templateUrl: './request-information.component.html',
  styleUrls: ['./request-information.component.scss'],
  animations: [slideInOutAnimation],
  host: { '[@slideInOutAnimation]': '' },
  standalone: true,
  imports: [...SHARED_IMPORTS],
})

export class RequestInformationComponent implements OnInit {
  public validationForm: UntypedFormGroup;
  public valuationData: valuationByHash;
  public customPickupAddress = false;
  public rdwCodesKnown = null;
  public submitted = false;
  public frontUploadOk = false;
  public backUploadOk = false;

  public showFrontUpload = true;
  public showBackUpload = true;

  public documentUploading = false;
  public uploadDocumentError = '';
  public submitError = null;
  public minDate = { year: 0, month: 0, day: 0 };
  public dateRange: any;

  constructor(
    public fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly api: ApiService,
    private readonly ngxService: NgxUiLoaderService,
    private readonly valuationService: ValuationService,
    private readonly route: ActivatedRoute,
  ) {

    // Set the min date for date selector
    const date = new Date();
    this.minDate.year = date.getFullYear();
    this.minDate.month = date.getMonth() + 1;
    this.minDate.day = date.getDay() + 1;

    localStorage.setItem('state', '/informatie-aanleveren');

    this.validationForm = fb.group({
      licenseCardFront: [null, [Validators.required]],
      licenseCardBack: [null, [Validators.required]],
      invoiceStreet: [null, [Validators.required, Validators.minLength(2)]],
      invoiceNumber: [null, [Validators.required]],
      invoiceNumberSuffix: [null],
      invoicePostalCode: [null, [Validators.required, Validators.pattern('^[0-9]{4}\s?[A-Za-z]{2}$')]],
      invoiceCity: [null, [Validators.required, Validators.minLength(2)]],
      pickupDate: ['', Validators.required],
      pickupStreet: [null],
      pickupNumber: [null],
      pickupNumberSuffix: [null],
      pickupPostalcode: [null],
      pickupCity: [null],
      iban: [null, [Validators.required, ValidatorService.validateIban]],
      bankAccountName: [null, Validators.required],
      agreement: [null, Validators.requiredTrue],
    });
  }

  public ngOnInit(): void {
    this.ngxService.start();
    // Get route params used in servcie
    this.route.params.subscribe((routeParams) => {
      // Sync data
      this.valuationService.syncValuationData(routeParams).then(data => {
        this.valuationData = data;

        // Check if verified has been set.
        if (localStorage.getItem('verified') == null) {
          this.router.navigate(['/verificatie']);
        } else {
          // Verified has been set, check time between now and checked.
          if (Number(new Date().getTime()) - Number(localStorage.getItem('verified')) < 900000) {
            this.ngxService.start();
            // Less than 15 minutes, do something. We are save to do api actions.
            this.api.getDateRange(moment().add(3, 'days').format('Y-MM-DD'), 14, 'sat,sun').subscribe(data => {
              this.dateRange = data.data
            })
            const valuationAuth: storedValudationData = JSON.parse(localStorage.getItem('valuation'));
            this.api.getValuationId(valuationAuth.id).subscribe(data => {
              this.ngxService.stop();

              // Check if documents have been uploaded.
              if (data.data.Documents) {
                for (const document of data.data.Documents) {
                  if (document.type == "registrationcard_front") {
                    // Clear validators for this form.
                    this.validationForm.controls['licenseCardFront'].setValidators(null);
                    this.validationForm.controls['licenseCardFront'].updateValueAndValidity();
                    this.showFrontUpload = false;
                  } else if (document.type == "registrationcard_back") {
                    // Clear validators for this form.
                    this.validationForm.controls['licenseCardBack'].setValidators(null);
                    this.validationForm.controls['licenseCardBack'].updateValueAndValidity();
                    this.showBackUpload = false;
                  }
                }
              }

              if (data.data.additionalDataProvided == true) {
                this.router.navigate(['/status']);
              }
            })
          } else {
            // More than 15 minutes, re-verify.
            this.router.navigate(['/verificatie']);
          }
        }
      }, error => {
        console.error(error);
      })
    });
  }

  public get f() { return this.validationForm.controls; }

  public uploadValuationDocument(type: string, file: FileList) {
    const reader = new FileReader();
    this.documentUploading = true;

    let blob: Blob = file[0];

    // Check the type of file dropped.
    if (file[0].name.split('.').pop() == 'HEIC') {
      // HEIC image selected. We have some conversion to do!
      heic2any({ blob, toType: "image/jpeg", quality: 0.99 }).then((jpgBlob: Blob) => {
        reader.readAsDataURL(jpgBlob);
        reader.onload = () => {
          this.uploadDocument(type, reader.result)
        }
      });
    } else {
      // Assume the file is either JPEG or PNG. No conversion to do.
      reader.readAsDataURL(blob);
      reader.onload = () => {
        this.uploadDocument(type, reader.result)
      };
    }
  }

  public uploadDocument(type: string, file: any) {
    const valuationAuth: storedValudationData = JSON.parse(localStorage.getItem('valuation'));
    let submitData: addValuationDocument = {
      documentContent: file,
      type: type
    };

    this.api.addValuationDocument(valuationAuth.id, submitData).subscribe(data => {
      if (data.error == false) {
        this.documentUploading = false;
        if (type == 'registrationcard_front') {
          // Clear validators for this form.
          this.validationForm.controls['licenseCardFront'].setValidators(null);
          this.frontUploadOk = true;
          this.showFrontUpload = false;
        } else if (type == 'registrationcard_back') {
          // Clear validators for this form.
          this.validationForm.controls['licenseCardBack'].setValidators(null);
          this.backUploadOk = true;
          this.showBackUpload = false;
        }
      } else {
        this.documentUploading = false;
        this.uploadDocumentError = data.message;
      }
    })
  }

  public onSubmit() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.validationForm.invalid) {
      return;
    } else {
      // Upload form data
      const valuationAuth: storedValudationData = JSON.parse(localStorage.getItem('valuation'));
      const submitData: updateValuationCustomer = {
        street: this.validationForm.controls['invoiceStreet'].value,
        number: this.validationForm.controls['invoiceNumber'].value,
        suffix: this.validationForm.controls['invoiceNumberSuffix'].value,
        postalCode: this.validationForm.controls['invoicePostalCode'].value,
        city: this.validationForm.controls['invoiceCity'].value,
        iban: this.validationForm.controls['iban'].value,
        bankAccountName: this.validationForm.controls['bankAccountName'].value,
      }

      // Show loading
      this.ngxService.start();

      // Get customer data by using "normal" valuation call.
      this.api.getValuationId(valuationAuth.id).subscribe(valuation => {

        // Push Tag manager purchase event
        window['dataLayer'] = window['dataLayer'] || [];
        window['dataLayer'].push({ value: this.mapConversionValue(valuation.data.dgwAmount) });
        window['dataLayer'].push({ currency: 'EUR' });
        window['dataLayer'].push({ valuation_id: valuation.data.id });
        window['dataLayer'].push({ email: valuation.data.Customer.email });
        window['dataLayer'].push({ phone: valuation.data.Customer.phone });
        window['dataLayer'].push({ event: 'accept_bid' });

        // Set the selected delivery date
        this.updateDateDelivery().then(data => {
          // Update delivery address for transport. (uses invoice address if not changed)
          // Pass customer data to function that updates transport address
          this.updateDeliveryAddress(valuation.data).then(data => {
            // Submit customer information
            this.api.updateValuationCustomer(valuationAuth.id, submitData).subscribe(data => {
              if (data.error == false) {
                localStorage.setItem('state', '/bedankt');
                this.router.navigate(['/bedankt']);
                this.ngxService.stop();
              } else {
                this.ngxService.stop();
                this.submitError = JSON.stringify(data.message);
                // Scroll to top to show error to user.
                window.scroll(0, 0);
              }
            }, error => {
              this.ngxService.stop();
            })
          });
        });
      })
    }
  }

  public updateDeliveryAddress(valuation: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const valuationAuth: storedValudationData = JSON.parse(localStorage.getItem('valuation'));
      // Changed to inspection Address instead of transport address.
      let submitData: setInspectionAddress;

      let fullName = '';
      if (valuation.Customer.infix != null) {
        // Infix is set
        fullName = valuation.Customer.firstName + ' ' + valuation.Customer.infix + ' ' + valuation.Customer.lastName;
      } else {
        // No infix set.
        fullName = valuation.Customer.firstName + ' ' + valuation.Customer.lastName;
      }

      // User selected other pickup address.
      if (this.customPickupAddress == true) {
        submitData = {
          //type: "load",
          country: "Nederland",
          companyName: null,
          personName: fullName,
          street: this.validationForm.controls['pickupStreet'].value,
          number: this.validationForm.controls['pickupNumber'].value + this.validationForm.controls['pickupNumberSuffix'].value,
          zipcode: this.validationForm.controls['pickupPostalcode'].value,
          city: this.validationForm.controls['pickupCity'].value,
          phone: valuation.Customer.phone.replace(/\s/g, "")
        }
      } else {
        // Use the default address (invoice)
        submitData = {
          //type: "load",
          country: "Nederland",
          companyName: null,
          personName: fullName.trim(),
          street: this.validationForm.controls['invoiceStreet'].value,
          number: this.validationForm.controls['invoiceNumber'].value + this.validationForm.controls['invoiceNumberSuffix'].value,
          zipcode: this.validationForm.controls['invoicePostalCode'].value,
          city: this.validationForm.controls['invoiceCity'].value,
          phone: valuation.Customer.phone.replace(/\s/g, "")
        }
      }
      this.api.setInspectionAddress(valuationAuth.id, submitData).subscribe(data => {
        resolve(true);
      }, error => {
        reject(false);
      });
      resolve(true);
    });
  }

  public updateDateDelivery(): Promise<any> {
    return new Promise((resolve, reject) => {
      const valuationAuth: storedValudationData = JSON.parse(localStorage.getItem('valuation'));
      const selectedDate = moment(this.validationForm.controls['pickupDate'].value);
      const submitData: updateDeliveryDate = {
        dateDelivery: selectedDate.format("DD-MM-yyyy")
      }

      this.api.updateDateDelivery(valuationAuth.id, submitData).subscribe(data => {
        resolve(true)
      }, error => {
        reject(false)
      })
    })
  }

  public handleRDWChange(value: any) {
    this.rdwCodesKnown = value;
  }

  public handleInvoiceAddressChange(value: any) {
    this.customPickupAddress = value;
  }

  public onReset() {
    this.validationForm.reset();
  }

  public mapConversionValue(value: number) {
    if (value >= 0 && value <= 5000) {
      return 747.00;
    } else if (value >= 5001 && value <= 10000) {
      return 828.00
    } else if (value >= 10001 && value <= 15000) {
      return 976.00
    } else if (value >= 15001 && value <= 20000) {
      return 865.00
    } else if (value >= 20001 && value <= 30000) {
      return 1146.00
    } else if (value >= 30001 && value <= 40000) {
      return 1415.00
    } else {
      return 2244.00
    }
  }
}
